import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles.css';

function MobileScrollInput({ value, onChange, step = '1', min, max, unit, position, id, isChanged = false, isFourCornerGroup = false, showUnitInline = false, isSnapshotUpdate = false, attributedToOther = false, attributionTitle = '', attributionColor = '' }) {
  const isTextInput = unit === 'text';
  const [isActive, setIsActive] = useState(false);
  const [startY, setStartY] = useState(null);
  const [tempValue, setTempValue] = useState(value || '');
  const iconRef = useRef(null);
  const parsedStep = parseFloat(step) || 1;
  const parsedMin = min !== undefined ? parseFloat(min) : undefined;
  const parsedMax = max !== undefined ? parseFloat(max) : undefined;
  const decimalPlaces = parsedStep < 1 ? (step.toString().split('.')[1]?.length || 1) : 2;

  const roundToStep = (numValue) => {
    if (isNaN(numValue)) return parsedMin !== undefined ? parsedMin.toFixed(decimalPlaces) : '';
    const steps = Math.round((numValue - (parsedMin || 0)) / parsedStep);
    let newValue = (parsedMin || 0) + steps * parsedStep;
    if (parsedMin !== undefined && newValue < parsedMin) newValue = parsedMin;
    if (parsedMax !== undefined && newValue > parsedMax) newValue = parsedMax;
    return newValue.toFixed(decimalPlaces);
  };

  const adjustValue = useCallback((direction) => {
    if (isTextInput) return;
    let numValue = parseFloat(tempValue) || 0;
    numValue += direction * parsedStep;

    // Ensure smooth transition across zero
    if (parsedMin !== undefined && numValue < parsedMin) {
      numValue = parsedMin;
    } else if (parsedMax !== undefined && numValue > parsedMax) {
      numValue = parsedMax;
    }

    const finalValue = numValue.toFixed(decimalPlaces);
    setTempValue(finalValue);
    onChange(finalValue);
  }, [isTextInput, tempValue, parsedMin, parsedMax, parsedStep, decimalPlaces, onChange]);

  const handleTouchStart = useCallback((e, direction) => {
    if (isTextInput) return;
    setStartY(e.touches[0].clientY);
    e.preventDefault();
    e.stopPropagation();
  }, [isTextInput]);

  const handleTouchMove = useCallback((e) => {
    if (isTextInput) return;
    if (startY === null) return;
    const currentY = e.touches[0].clientY;
    const deltaY = startY - currentY;
    if (Math.abs(deltaY) > 25) {
      const sign = deltaY > 0 ? 1 : -1;
      adjustValue(sign);
      setStartY(currentY);
    }
    e.preventDefault();
    e.stopPropagation();
  }, [isTextInput, startY, adjustValue]);

  const handleTouchEnd = useCallback((e) => {
    if (isTextInput) return;
    setStartY(null);
    e.preventDefault();
    e.stopPropagation();
  }, [isTextInput]);

  const handleWheel = useCallback((e) => {
    if (isTextInput) return;
    e.preventDefault();
    e.stopPropagation();
    const sign = e.deltaY < 0 ? 1 : -1;
    adjustValue(sign);
  }, [isTextInput, adjustValue]);

  const handleIconClick = useCallback((e, direction) => {
    if (isTextInput) return;
    e.stopPropagation();
    if (!isActive) {
      setIsActive(true);
      const event = new CustomEvent('deactivateOtherScrollInputs', { detail: { id: `scroll-input-${id}` } });
      document.dispatchEvent(event);
    }
  }, [isTextInput, isActive, id]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setTempValue(newValue);
    if (isTextInput) {
      onChange(newValue);
    }
  };

  const handleBlur = () => {
    if (isTextInput) return;
    if (tempValue === '') {
      setTempValue('');
      onChange('');
      return;
    }
    const numValue = parseFloat(tempValue);
    if (!isNaN(numValue)) {
      const hasDecimal = tempValue.includes('.');
      const roundedValue = roundToStep(numValue);
      const finalValue = !hasDecimal && parsedStep >= 1 ? parseFloat(roundedValue).toFixed(2) : roundedValue;
      setTempValue(finalValue);
      onChange(finalValue);
    } else {
      setTempValue('');
      onChange('');
    }
  };

  useEffect(() => {
    setTempValue(value || '');
  }, [value]);

  useEffect(() => {
    const handleDeactivate = (e) => {
      if (e.detail.id !== `scroll-input-${id}`) {
        setIsActive(false);
      }
    };
    document.addEventListener('deactivateOtherScrollInputs', handleDeactivate);
    return () => {
      document.removeEventListener('deactivateOtherScrollInputs', handleDeactivate);
    };
  }, [id]);

  useEffect(() => {
    if (isTextInput || !isActive) return;
    const icon = iconRef.current;
    if (icon) {
      const touchStart = (e) => handleTouchStart(e, position && position.includes('Left') ? 'up' : 'down');
      icon.addEventListener('touchstart', touchStart, { passive: false });
      icon.addEventListener('touchmove', handleTouchMove, { passive: false });
      icon.addEventListener('touchend', handleTouchEnd, { passive: false });
      icon.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        icon.removeEventListener('touchstart', touchStart);
        icon.removeEventListener('touchmove', handleTouchMove);
        icon.removeEventListener('touchend', handleTouchEnd);
        icon.removeEventListener('wheel', handleWheel);
      };
    }
  }, [isActive, startY, position, isTextInput, handleTouchStart, handleTouchMove, handleTouchEnd, handleWheel]);

  const isLeftAligned = position && position.includes('Left');
  return (
    <div className={`scroll-input-container ${isTextInput ? 'text-align' : isLeftAligned ? 'left-align' : 'right-align'} ${!isFourCornerGroup && !isTextInput ? 'single-input' : ''}`}>
      {position && !isFourCornerGroup && <span>{position}</span>}
      {!isTextInput && isLeftAligned && (
        <span
          ref={iconRef}
          className={`material-icons scroll-indicator ${isActive ? 'active' : ''}`}
          onClick={(e) => handleIconClick(e, 'up')}
        >
          swap_vert
        </span>
      )}
      <input
        type={isTextInput ? 'text' : 'number'}
        value={tempValue}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`scroll-input ${isChanged ? 'changed' : ''} ${isSnapshotUpdate ? 'snapshot-update' : ''} ${isTextInput ? 'text-input' : ''} ${attributedToOther ? 'edited-by-other' : ''}`}
        title={attributedToOther ? attributionTitle : undefined}
        style={attributedToOther && attributionColor ? { backgroundColor: attributionColor, borderColor: attributionColor, boxShadow: `inset 0 0 0 1px ${attributionColor}` } : undefined}
        step={isTextInput ? undefined : step}
        min={isTextInput ? undefined : min}
        max={isTextInput ? undefined : max}
      />
      {!isTextInput && !isLeftAligned && (
        <span
          ref={iconRef}
          className={`material-icons scroll-indicator ${isActive ? 'active' : ''}`}
          onClick={(e) => handleIconClick(e, 'down')}
        >
          swap_vert
        </span>
      )}
      {showUnitInline && unit && <span className="inline-unit">{unit}</span>}
    </div>
  );
}

export default MobileScrollInput;